import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Users, Clock, AlertCircle, CheckCircle2, XCircle, RefreshCw, TrendingUp, TrendingDown, Bell, Filter, Search, Eye, EyeOff, Edit, Save, Plus, Trash2, Info, HelpCircle, ExternalLink, Copy, Globe, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Zap, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Activity, BarChart3, PieChart, LineChart, ScrollText, Database, HardDrive, Cpu, MemoryStick, Network, Server, Wrench, Shield, Archive, Download, Upload, List, Play, Pause, Stop, ArrowUp, ArrowDown, ArrowRight, ArrowLeft, RotateCcw, Settings, Target as TargetIcon, Zap as ZapIcon, Brain as BrainIcon, Route as RouteIcon, UserCheck, UserX, UserPlus, UserMinus, AlertTriangle, Clock as ClockIcon, Timer as TimerIcon, Calendar, ChevronUp, ChevronDown, ChevronRight, ChevronLeft
} from 'lucide-react';
import { AgentService } from '../../services/AgentService';

const TicketEscalation = () => {
  const [escalationMetrics, setEscalationMetrics] = useState(null);
  const [escalatedTickets, setEscalatedTickets] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [escalationHistory, setEscalationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [escalationReason, setEscalationReason] = useState('');
  const [escalationLevel, setEscalationLevel] = useState('level2');

  const loadEscalationData = useCallback(async () => {
    try {
      setLoading(true);
      const [metricsResponse, ticketsResponse, rulesResponse, historyResponse] = await Promise.all([
        AgentService.getEscalationMetrics(),
        AgentService.getEscalatedTickets(),
        AgentService.getEscalationRules(),
        AgentService.getEscalationHistory()
      ]);

      if (metricsResponse.success) setEscalationMetrics(metricsResponse.data);
      if (ticketsResponse.success) setEscalatedTickets(ticketsResponse.data);
      if (rulesResponse.success) setEscalationRules(rulesResponse.data);
      if (historyResponse.success) setEscalationHistory(historyResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEscalationData();
  }, [loadEscalationData]);

  const handleEscalateTicket = async (ticketId, level, reason) => {
    try {
      const response = await AgentService.escalateTicket(ticketId, level, reason);
      if (response.success) {
        await loadEscalationData();
        setSelectedTicket(null);
        setEscalationReason('');
        setEscalationLevel('level2');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeEscalateTicket = async (ticketId) => {
    try {
      const response = await AgentService.deEscalateTicket(ticketId);
      if (response.success) {
        await loadEscalationData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'urgent': return 'bg-red-800 text-red-50';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEscalationLevelColor = (level) => {
    switch (level) {
      case 'level1': return 'bg-yellow-100 text-yellow-800';
      case 'level2': return 'bg-orange-100 text-orange-800';
      case 'level3': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEscalationIcon = (level) => {
    switch (level) {
      case 'level1': return <ChevronUp className="w-4 h-4 text-yellow-600" />;
      case 'level2': return <ChevronUp className="w-4 h-4 text-orange-600" />;
      case 'level3': return <ChevronUp className="w-4 h-4 text-red-600" />;
      default: return <ChevronUp className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading escalation data</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ticket Escalation Management</h1>
          <p className="text-gray-600">Manage ticket escalations and escalation workflows</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadEscalationData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: LayoutDashboard },
            { id: 'tickets', name: 'Escalated Tickets', icon: FileText },
            { id: 'rules', name: 'Escalation Rules', icon: Settings },
            { id: 'history', name: 'Escalation History', icon: ScrollText }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && escalationMetrics && (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {escalationMetrics.overviewMetrics.map((metric, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${
                    metric.status === 'good' ? 'bg-green-100' :
                    metric.status === 'warning' ? 'bg-yellow-100' :
                    'bg-red-100'
                  }`}>
                    {metric.status === 'good' ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : metric.status === 'warning' ? (
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  {metric.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-red-500 mr-1" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-green-500 mr-1" />
                  )}
                  <span className={`text-sm ${
                    metric.trend === 'up' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Escalation Levels Distribution */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Escalation Levels Distribution</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {escalationMetrics.escalationLevels.map((level, index) => (
                  <div key={index} className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                      level.level === 'level1' ? 'bg-yellow-100' :
                      level.level === 'level2' ? 'bg-orange-100' :
                      'bg-red-100'
                    }`}>
                      {getEscalationIcon(level.level)}
                    </div>
                    <h4 className="text-lg font-medium text-gray-900">{level.name}</h4>
                    <p className="text-sm text-gray-600">{level.count} tickets</p>
                    <p className={`text-sm font-medium mt-1 ${
                      level.level === 'level1' ? 'text-yellow-600' :
                      level.level === 'level2' ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {level.percentage}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Escalations */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Escalations</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {escalationMetrics.recentEscalations.map((escalation, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {getEscalationIcon(escalation.level)}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Ticket #{escalation.ticketId}</p>
                        <p className="text-sm text-gray-600">{escalation.reason}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">{escalation.timestamp}</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEscalationLevelColor(escalation.level)}`}>
                        {escalation.level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Escalated Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="space-y-6">
          {/* Escalated Tickets List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Escalated Tickets</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Escalation Level</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Escalated By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Escalated At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {escalatedTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{ticket.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                        {ticket.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEscalationLevelColor(ticket.escalationLevel)}`}>
                          {getEscalationIcon(ticket.escalationLevel)}
                          <span className="ml-1">{ticket.escalationLevel}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ticket.escalatedBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ticket.escalatedAt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setSelectedTicket(ticket)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeEscalateTicket(ticket.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Escalation Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-6">
          {/* Escalation Rules List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Escalation Rules</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {escalationRules.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-full ${
                        rule.enabled ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {rule.enabled ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">{rule.name}</h4>
                        <p className="text-sm text-gray-600">{rule.description}</p>
                        <div className="mt-1 flex items-center space-x-4">
                          <span className="text-xs text-gray-500">Trigger: {rule.trigger}</span>
                          <span className="text-xs text-gray-500">Level: {rule.targetLevel}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rule.priority === 'high' ? 'bg-red-100 text-red-800' :
                        rule.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {rule.priority}
                      </span>
                      <button className="text-gray-600 hover:text-gray-900">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Escalation History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Escalation History */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Escalation History</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {escalationHistory.map((history, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-full ${
                        history.action === 'escalated' ? 'bg-red-100' : 'bg-green-100'
                      }`}>
                        {history.action === 'escalated' ? (
                          <ChevronUp className="w-4 h-4 text-red-600" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Ticket #{history.ticketId}</p>
                        <p className="text-sm text-gray-600">{history.action === 'escalated' ? 'Escalated' : 'De-escalated'} by {history.userName}</p>
                        <p className="text-sm text-gray-500">{history.reason}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">{history.timestamp}</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        history.action === 'escalated' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {history.action}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Escalation Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Escalate Ticket #{selectedTicket.id}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Escalation Level</label>
                  <select
                    value={escalationLevel}
                    onChange={(e) => setEscalationLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="level1">Level 1 - Supervisor</option>
                    <option value="level2">Level 2 - Manager</option>
                    <option value="level3">Level 3 - Director</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Escalation Reason</label>
                  <textarea
                    value={escalationReason}
                    onChange={(e) => setEscalationReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter escalation reason..."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleEscalateTicket(selectedTicket.id, escalationLevel, escalationReason)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Escalate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketEscalation;
